/**
 * AchievementToast - Notificacion de logro desbloqueado
 *
 * Se muestra cuando el usuario desbloquea un nuevo logro.
 * Celebra sin interrumpir demasiado el flujo.
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { cn } from '@/core/utils';
import type { Achievement } from '@/services/achievements';
import { feedbackSuccess } from '@/services/feedback';

interface AchievementToastProps {
  achievement: Achievement | null;
  onDismiss: () => void;
}

const TIER_COLORS = {
  bronze: 'from-orange-600 to-orange-800 border-orange-500',
  silver: 'from-slate-400 to-slate-600 border-slate-400',
  gold: 'from-yellow-500 to-yellow-700 border-yellow-500',
  platinum: 'from-cyan-400 to-cyan-600 border-cyan-400',
};

export function AchievementToast({ achievement, onDismiss }: AchievementToastProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (achievement) {
      setIsVisible(true);
      feedbackSuccess();

      // Auto-dismiss despues de 5 segundos
      const timeout = setTimeout(() => {
        setIsVisible(false);
        setTimeout(onDismiss, 300); // Esperar animacion
      }, 5000);

      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [achievement, onDismiss]);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(onDismiss, 300);
  };

  return (
    <AnimatePresence>
      {isVisible && achievement && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          className="fixed bottom-4 left-4 right-4 z-[300] sm:left-auto sm:right-4 sm:max-w-sm"
        >
          <div
            className={cn(
              'relative overflow-hidden rounded-xl border shadow-2xl',
              'bg-gradient-to-br',
              TIER_COLORS[achievement.tier]
            )}
          >
            {/* Efecto de brillo */}
            <motion.div
              className="absolute inset-0 bg-white/20"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ duration: 0.6, delay: 0.2 }}
            />

            <div className="relative p-4">
              {/* Header */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-white/80" />
                  <span className="text-xs font-medium text-white/80 uppercase tracking-wide">
                    Logro desbloqueado
                  </span>
                </div>
                <button
                  onClick={handleDismiss}
                  className="p-1 rounded-full hover:bg-white/20 transition-colors"
                >
                  <X className="w-4 h-4 text-white/70" />
                </button>
              </div>

              {/* Contenido */}
              <div className="mt-3 flex items-center gap-4">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', delay: 0.1 }}
                  className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center text-3xl"
                >
                  {achievement.icon}
                </motion.div>
                <div className="flex-1">
                  <h3 className="font-bold text-white text-lg">{achievement.title}</h3>
                  <p className="text-sm text-white/80 mt-0.5">{achievement.description}</p>
                </div>
              </div>

              {/* Mensaje de celebracion */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mt-3 text-sm text-white/90 italic"
              >
                "{achievement.celebrationMessage}"
              </motion.p>

              {/* Barra de progreso animada (decorativa) */}
              <motion.div
                className="absolute bottom-0 left-0 h-1 bg-white/30"
                initial={{ width: '100%' }}
                animate={{ width: '0%' }}
                transition={{ duration: 5, ease: 'linear' }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
